import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const contextParallelComponent: ComponentDef = {
  id: 'context-parallel',
  name: 'Context Parallel',
  description: 'Ring Attention: shard sequence across P GPUs, ring-pass K/V blocks between ranks, accumulate partial attention outputs — enabling 1M+ token context training.',
  category: 'parallelism',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 8192, min: 1, max: 131072, step: 1 },
    { name: 'cp_size', label: 'CP size (ring size P)', type: 'number', default: 4, min: 2, max: 16, step: 1 }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const cp_size = Number(params.cp_size);
    const shard_len = Math.floor(seq_len / cp_size);

    const g = new GraphBuilder();
    g.input('x_full', [seq_len, d_model]);

    // ── Step 1: Split sequence into P shards, each rank owns one ──
    g.split('x_shard', 'x_full', [shard_len, d_model], `seq / ${cp_size} per rank`);

    // ── Step 2: Local QKV projection on this rank's shard ──
    g.linear('Q_local', 'x_shard', [d_model, d_model]);
    g.linear('K_local', 'x_shard', [d_model, d_model]);
    g.linear('V_local', 'x_shard', [d_model, d_model]);

    // ── Step 3: Local attention (this rank's Q attends to local K/V) ──
    g.transpose('K_local_T', 'K_local', [d_model, shard_len], 'K^T');
    g.matmul('QK_local', 'Q_local', 'K_local_T', [shard_len, shard_len]);
    g.scale('QK_scaled', 'QK_local', '1/√d_k');
    g.softmax('attn_local', 'QK_scaled', -1);
    g.matmul('O_local', 'attn_local', 'V_local', [shard_len, d_model]);

    // ── Step 4: Ring pass — send K/V to next rank, receive from prev rank ──
    // Repeat P-1 times; each step attends to a remote shard
    g.p2p('K_recv', 'K_local', [shard_len, d_model]);     // receive K from prev rank
    g.p2p('V_recv', 'V_local', [shard_len, d_model]);     // receive V from prev rank

    // ── Step 5: Remote attention with received K/V ──
    g.transpose('K_recv_T', 'K_recv', [d_model, shard_len], 'K_remote^T');
    g.matmul('QK_remote', 'Q_local', 'K_recv_T', [shard_len, shard_len]);
    g.scale('QK_remote_scaled', 'QK_remote', '1/√d_k  (causal mask: skip upper ranks)');
    g.softmax('attn_remote', 'QK_remote_scaled', -1);
    g.matmul('O_remote', 'attn_remote', 'V_recv', [shard_len, d_model]);

    // ── Step 6: Accumulate local + remote partial outputs ──
    g.add('O_accum', 'O_local', 'O_remote');

    // ── Step 7: After P-1 ring steps, all-reduce the softmax normaliser ──
    g.allreduce('O_final', 'O_accum', [shard_len, d_model]);

    // ── Output projection ──
    g.linear('out_proj', 'O_final', [d_model, d_model]);
    g.output('out', 'out_proj');
    return g.build();
  }
};
